import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';

import {
  CREATE_SECTION,
  DELETE_SECTION,
  UPDATE_SECTION
} from './../../gql';

import Accordion from './../../components/Accordion';
import Throbber from './../../components/Throbber';
import { addToChildList, removeFromChildList } from './../../utils/cache';
import Subsections from './Subsections';

import roundPlus from './../../../images/icons/round-plus-white.svg';

class Sections extends Component {
  constructor() {
    super();
    this.state = {
      sectionUpdate: {},
      publishIsUpToDate: null,
      isCreating: false,
      createError: null,
      deletingId: null,
      deleteError: null
    }
  }

  componentDidUpdate(prevProps) {
    const { published } = this.props;

    if (!published && prevProps.published) {
      this.setState({
        publishIsUpToDate: false
      });
    }

    if (published && !prevProps.published) {
      this.setState({
        publishIsUpToDate: true
      });
    }
  }

  parentList() {
    const { articleId } = this.props;
    return {
      parentType: 'Article',
      parentId: articleId,
      field: 'sections'
    };
  }

  createSection(sections) {
    const {
      articleId,
      app,
      client,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert
    } = this.props;

    const orderIndex = sections.length > 0
      ? sections[sections.length - 1].meta.displayOrder + 1
      : 0;

    this.setState({ isCreating: true, createError: null });
    client.mutate({
      mutation: CREATE_SECTION,
      variables: {
        title: '',
        subtitle: '',
        articleId: articleId,
        languageCode: languageCode,
        app: app,
        displayOrder: orderIndex
      },
      update: (cache, { data }) => addToChildList(cache, this.parentList(), data.createSection)
    })
      .then(() => this.setState({ isCreating: false }))
      .catch(error => this.setState({
        isCreating: false,
        createError: error.message
      }));
    activatePublishButton();
    activateUnpublishedAlert();
  }

  updateSection(variables) {
    const { client } = this.props;

    // the mutation returns the updated section, which Apollo merges into the cache
    client.mutate({
      mutation: UPDATE_SECTION,
      variables
    }).catch(() => {
      // errors are logged by the errorLink
    });
  }

  submitUpdate() {
    const {
      activatePublishButton,
      activateUnpublishedAlert
    } = this.props;
    const { sectionUpdate } = this.state;
    this.updateSection(sectionUpdate);
    this.setState({
      sectionUpdate: {}
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onChangeHandle(event, id) {
    const {
      app,
      languageCode,
    } = this.props;
    const name = event.target.name;
    const value = event.target.value;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.submitUpdate(), 500);

    this.setState(prevState => {
      if (prevState.sectionUpdate && prevState.sectionUpdate.id === id) {
        return {
          sectionUpdate: {
            ...this.state.sectionUpdate,
            id,
            [name]: value
          }
        }
      }
      return {
        sectionUpdate: {
          app,
          languageCode,
          published: false,
          id,
          [name]: value
        }
      }
    });
  }

  deleteSection(id) {
    const {
      client,
      activatePublishButton,
      activateUnpublishedAlert
    } = this.props;
    if (confirm('Are you sure you want to delete this section?')) {
      this.setState({ deletingId: id, deleteError: null });
      client.mutate({
        mutation: DELETE_SECTION,
        variables: { id },
        update: cache => removeFromChildList(cache, this.parentList(), id)
      })
        .then(() => this.setState({ deletingId: null }))
        .catch(error => this.setState({
          deletingId: null,
          deleteError: { id, message: error.message }
        }));
      activatePublishButton();
      activateUnpublishedAlert();
    }
  }

  updateDisplayOrder(id, order) {
    const {
      app,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert
    } = this.props;

    this.updateSection({
      id,
      app,
      languageCode,
      published: false,
      displayOrder: order
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  publishNotification() {
    const { activatePublishButton } = this.props;
    const { publishIsUpToDate } = this.state;

    if (publishIsUpToDate === null) {
      activatePublishButton();
    } else if (publishIsUpToDate) {
      return;
    }

    return (
      <div className="notification notification--warning">
        <p className="notification__description">
          The content below has not been published yet.
        </p>
      </div>
    );
  }

  render() {
    const {
      sections,
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert
    } = this.props;
    const {
      isCreating,
      createError,
      deletingId,
      deleteError
    } = this.state;

    return (
      <div>
        {sections
          .sort((a, b) => a.meta.displayOrder - b.meta.displayOrder)
          .map(section =>
            <div key={section.id} className="relative-block">
              {deleteError && deleteError.id === section.id &&
                <p className="full-width center-align">{deleteError.message}</p>
              }
              {!section.availability.published &&
                this.publishNotification()
              }
              <Accordion
                lazyChildren
                id={section.id}
                title={section.title}
                subtitle={section.subtitle}
                twoHeaderInputs
                enableOrdering
                displayOrder={section.meta.displayOrder}
                siblingAccordions={sections}
                updateDisplayOrder={(id, order) => this.updateDisplayOrder(id, order)}
                deleteAccordion={() => this.deleteSection(section.id)}
                onChanges={event => this.onChangeHandle(event, section.id)}>
                <Subsections
                  sectionId={section.id}
                  subsections={section.subsections}
                  languageCode={languageCode}
                  published={published}
                  app={app}
                  activatePublishButton={activatePublishButton}
                  activateUnpublishedAlert={activateUnpublishedAlert}
                />
              </Accordion>
              <Throbber
                hidden={deletingId !== section.id}
                loadingText="Deleting section..."
              />
            </div>
          )}
        <div className="full-width right-align relative-block">
          <Throbber
            hidden={!isCreating}
            loadingText="Creating section..."
          />
          {createError &&
            <p className="full-width center-align">{createError}</p>
          }
          <button
            className="button button--small button--icon-text"
            disabled={isCreating}
            onClick={() => this.createSection(sections)}>
            <img src={roundPlus} alt="Add new section" className="button__icon" />
            Add new section
          </button>
        </div>
      </div>
    );
  }
}

Sections.propTypes = {
  sections: PropTypes.array.isRequired,
  articleId: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  published: PropTypes.bool.isRequired,
  app: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired,
  activatePublishButton: PropTypes.func.isRequired,
  activateUnpublishedAlert: PropTypes.func.isRequired
}

export default withApollo(Sections);